import { Box, useMediaQuery, useTheme } from '@material-ui/core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../reduxhooks';
import { fetchNewProductId, fetchProductReviews } from '../redux/ProductReviewsRedux';
import { postNewProductAnalyticsLog } from '../redux/ProductAnalyticsRedux';
import { ProductMediaContext, ProductPageContext, VariantSelectionContext } from './ProductPageContext';
import ProductPageBody from './ProductPageBody';
import PageProductOverlayImageCarousel from './PageProductOverlayImageCarousel';
import { resolveProductLayout } from './productPageLayout';

// The product detail page, driven by the merchant's layout.
//
// Which blocks appear, and in what order, comes from the ProductTemplate
// this product is assigned to (or the store's default). The page itself
// owns only what no single block can: the product in scope, the shared
// gallery/lightbox state, and page-level analytics.
//
// Replaces the 3000-line hardcoded fareast page. Everything that page did
// beyond this — body-measurement sizing, the XXS–XXL size grid, marketplace
// links, and the hardcoded studiosclo.uk promo media — was fareast-specific
// and is gone.

const AUTOSLIDE_MS = 4300;
// How long a shopper interaction holds off the autoslide.
const AUTOSLIDE_FREEZE_MS = 9000;

export default function PageProductDetailBlocks() {
  const params = useParams<{ productId: string }>();
  const productId = decodeURIComponent(params.productId ?? '');

  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  const product = useAppSelector((state) => state.productsList.productsDictionary[productId]);
  const templatesById = useAppSelector((state) => state.productsList.productTemplatesById);
  const defaultTemplateId = useAppSelector((state) => state.productsList.defaultProductTemplateId);

  const [mediaIndex, setMediaIndex] = useState(0);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const nextAutoslideTime = useRef(0);

  const [selectedOptions, setSelectedOptions] =
    useState<{ [optionName: string]: string }>({});

  // The variant the page should DISPLAY (price block, gallery). Only defined
  // once every option is chosen and a variant matches — partial selections
  // keep showing the base product. Option-less products also stay undefined
  // here so their price display cannot contradict the listing cards; the
  // picker still resolves variants[0] for add-to-cart on its own.
  const displayVariant = useMemo(() => {
    const commerce = product?.commerce;
    if (!commerce || !commerce.options.length) return undefined;
    const options = [...commerce.options].sort((a, b) => a.position - b.position);
    if (!options.every((o) => selectedOptions[o.name])) return undefined;
    return commerce.variants.find((v) => {
      const values = [v.option1, v.option2, v.option3];
      return options.every((o, i) => values[i] === selectedOptions[o.name]);
    });
  }, [product, selectedOptions]);

  const variantImage = displayVariant?.imageSrc ?? null;

  // Product images are used verbatim. The old page rewrote every URL to
  // fareast's CDN convention (strip `_familymedia`, append `_1080.jpg`),
  // which 404s for terbebas-hosted media — every imported product's photos
  // were broken.
  //
  // A selected variant's own image is prepended when it isn't already among
  // the product images, so the gallery (and the lightbox, which receives the
  // same list) can land on it.
  const mediaUrls = useMemo(() => {
    const base = product?.productImages ?? [];
    if (variantImage && !base.includes(variantImage)) return [variantImage, ...base];
    return base;
  }, [product, variantImage]);

  // Jump the gallery to the selected variant's image, and hold off the
  // autoslide so the shopper's choice doesn't slide away.
  useEffect(() => {
    if (!variantImage) return;
    const index = mediaUrls.indexOf(variantImage);
    if (index === -1) return;
    setMediaIndex(index);
    nextAutoslideTime.current = Date.now() + AUTOSLIDE_FREEZE_MS;
  }, [variantImage, mediaUrls]);

  // Clamp when the list shrinks (a prepended variant image disappears once
  // the selection no longer resolves to that variant).
  useEffect(() => {
    setMediaIndex((current) => (current >= mediaUrls.length ? 0 : current));
  }, [mediaUrls]);

  // Reset the gallery and variant selection when navigating between products.
  useEffect(() => {
    setMediaIndex(0);
    setOverlayOpen(false);
    setSelectedOptions({});
  }, [productId]);

  useEffect(() => {
    if (!productId) return;
    dispatch(fetchNewProductId(productId));
    dispatch(fetchProductReviews(productId));
    dispatch(postNewProductAnalyticsLog({
      event: 'VISIT', productId, isMobileView: !isDesktop,
    }));
    // isDesktop deliberately excluded: a resize is not a new page view.
    // eslint-disable-next-line
  }, [productId, dispatch]);

  // Meta pixel view event, kept from the old page. index.html stubs fbq to
  // null rather than defining it, so the null check is load-bearing.
  useEffect(() => {
    if (!product) return;
    if ((window as any).fbq !== null && typeof (window as any).fbq === 'function') {
      (window as any).fbq('track', 'ViewContent', {
        content_ids: [product.productId],
        content_type: 'product',
        contents: [{ id: product.productId, quantity: 1 }],
        currency: 'IDR',
        value: Math.round(
          (product.discountedPrice === -1 ? product.originalPrice : product.discountedPrice) * 0.7),
      });
    }
  }, [product]);

  // Autoslide, paused while the lightbox is open, while a video is showing,
  // and for a few seconds after the shopper swipes.
  useEffect(() => {
    if (mediaUrls.length < 2) return;
    const id = setInterval(() => {
      if (Date.now() < nextAutoslideTime.current) return;
      setMediaIndex((current) => {
        if (mediaUrls[current]?.includes('mp4')) return current;
        return (current + 1) % mediaUrls.length;
      });
    }, AUTOSLIDE_MS);
    return () => clearInterval(id);
  }, [mediaUrls]);

  const mediaContext = useMemo(() => ({
    mediaUrls,
    index: mediaIndex,
    setIndex: setMediaIndex,
    overlayOpen,
    setOverlayOpen,
    freezeAutoslide: () => { nextAutoslideTime.current = Date.now() + AUTOSLIDE_FREEZE_MS; },
  }), [mediaUrls, mediaIndex, overlayOpen]);

  const variantContext = useMemo(() => ({
    selected: selectedOptions,
    setSelected: setSelectedOptions,
    displayVariant,
  }), [selectedOptions, displayVariant]);

  const layout = useMemo(
    () => resolveProductLayout(product?.productTemplateId, templatesById ?? {}, defaultTemplateId),
    [product, templatesById, defaultTemplateId]);

  // The catalog is still loading, or the id is not an ACTIVE product.
  if (!product) return null;

  return (
    <ProductPageContext.Provider value={{ productId, product }}>
      <ProductMediaContext.Provider value={mediaContext}>
      <VariantSelectionContext.Provider value={variantContext}>
        <Box>
          <ProductPageBody layout={layout} />
          {/* Fullscreen media viewer. Page chrome rather than a block: it
              is a portal over the whole page, and it shares the gallery's
              index so opening it lands on the slide being viewed. */}
          <PageProductOverlayImageCarousel
            updateNextAutoslideTime={() => {
              nextAutoslideTime.current = Date.now() + AUTOSLIDE_FREEZE_MS;
            }}
            imageOverlayIndex={mediaIndex}
            setImageOverlayIndex={setMediaIndex}
            imageOverlayOpen={overlayOpen}
            setImageOverlayOpen={setOverlayOpen}
            productImages={mediaUrls} />
        </Box>
      </VariantSelectionContext.Provider>
      </ProductMediaContext.Provider>
    </ProductPageContext.Provider>
  );
}
